import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './BookingRequests.css';
import { 
  getBookingRequests, 
  getBookingRequestById,
  shareBookingRequest,
  updateBookingRequestStatus 
} from '../../services/bookingRequestsService';
import { getAgencies } from '../../services/agenciesService';

// Map API response to component format
const mapBookingRequestFromAPI = (apiRequest = {}) => {
  // Format date from API (ISO format)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  // Map tracking status fields from API
  // The backend stores tracking information in the notes field as text entries
  // Format: [TRACKING] Start location reached at 2025-12-26T11:08:11.800Z
  const mapTrackingStatus = () => {
    // First, check if API returns direct tracking fields (snake_case or camelCase)
    const hasDirectFields = apiRequest.start_location_reached !== undefined || 
                           apiRequest.startLocationReached !== undefined ||
                           apiRequest.start_location_reached_at !== undefined;
    
    if (hasDirectFields) {
      // Use direct fields if available
      const startReached = apiRequest.start_location_reached !== undefined ? apiRequest.start_location_reached :
                          apiRequest.startLocationReached !== undefined ? apiRequest.startLocationReached :
                          apiRequest.start_location_reached_at ? true : false;
      
      const startDeparted = apiRequest.start_location_departed !== undefined ? apiRequest.start_location_departed :
                           apiRequest.startLocationDeparted !== undefined ? apiRequest.startLocationDeparted :
                           apiRequest.start_location_departed_at ? true : false;
      
      const destReached = apiRequest.destination_reached !== undefined ? apiRequest.destination_reached :
                         apiRequest.destinationReached !== undefined ? apiRequest.destinationReached :
                         apiRequest.destination_reached_at ? true : false;

      const formatTrackingTime = (timestamp) => {
        if (!timestamp) return null;
        if (timestamp === true) return null;
        try {
          return formatDate(timestamp);
        } catch (e) {
          return timestamp;
        }
      };

      return {
        startLocationReached: !!startReached,
        startLocationReachedTime: formatTrackingTime(apiRequest.start_location_reached_at || apiRequest.startLocationReachedAt),
        startLocationDeparted: !!startDeparted,
        startLocationDepartedTime: formatTrackingTime(apiRequest.start_location_departed_at || apiRequest.startLocationDepartedAt),
        destinationReached: !!destReached,
        destinationReachedTime: formatTrackingTime(apiRequest.destination_reached_at || apiRequest.destinationReachedAt)
      };
    }

    // Parse tracking information from notes field
    // Format: [TRACKING] Start location reached at 2025-12-26T11:08:11.800Z
    const parseTrackingFromNotes = (notes) => {
      if (!notes || typeof notes !== 'string') {
        return {
          startLocationReached: false,
          startLocationReachedTime: null,
          startLocationDeparted: false,
          startLocationDepartedTime: null,
          destinationReached: false,
          destinationReachedTime: null
        };
      }

      const trackingLines = notes.split('\n').filter(line => line.includes('[TRACKING]'));
      
      let startLocationReached = false;
      let startLocationReachedTime = null;
      let startLocationDeparted = false;
      let startLocationDepartedTime = null;
      let destinationReached = false;
      let destinationReachedTime = null;

      // Process tracking lines in chronological order
      trackingLines.forEach(line => {
        const match = line.match(/\[TRACKING\]\s+(.+?)\s+at\s+(.+)/);
        if (!match) return;

        const event = match[1].toLowerCase().trim();
        const timestamp = match[2].trim();

        if (event.includes('start location reached')) {
          startLocationReached = true;
          startLocationReachedTime = timestamp; // Keep the latest one
        } else if (event.includes('start location departed')) {
          startLocationDeparted = true;
          startLocationDepartedTime = timestamp; // Keep the latest one
        } else if (event.includes('destination delivered') || event.includes('destination reached')) {
          destinationReached = true;
          destinationReachedTime = timestamp; // Keep the latest one
        }
      });

      // Format timestamps
      const formatTrackingTime = (timestamp) => {
        if (!timestamp) return null;
        try {
          return formatDate(timestamp);
        } catch (e) {
          return timestamp;
        }
      };

      return {
        startLocationReached,
        startLocationReachedTime: formatTrackingTime(startLocationReachedTime),
        startLocationDeparted,
        startLocationDepartedTime: formatTrackingTime(startLocationDepartedTime),
        destinationReached,
        destinationReachedTime: formatTrackingTime(destinationReachedTime)
      };
    };

    return parseTrackingFromNotes(apiRequest.notes);
  };

  const trackingStatus = mapTrackingStatus();

  return {
    // Store both numeric id (for API calls) and request_id (for display)
    id: apiRequest.id || apiRequest.request_id || '',
    numericId: apiRequest.id || null, // Numeric ID for API calls
    requestId: apiRequest.request_id || apiRequest.id?.toString() || '', // Display ID
    name: apiRequest.name || '',
    email: apiRequest.email || '',
    phone: apiRequest.phone || '',
    fromLocation: apiRequest.from_location || '',
    toLocation: apiRequest.to_location || '',
    vehicleNumber: apiRequest.vehicle_type || '',
    requestedDate: formatDate(apiRequest.requested_date || apiRequest.created_at),
    status: apiRequest.status || 'Pending',
    notes: apiRequest.notes || '',
    sharedWithAgency: apiRequest.shared_with_agency_id || null,
    stops: apiRequest.stops || [],
    products: (apiRequest.products || []).map(product => ({
      productName: product.product_name || product.productName || '',
      productCategory: product.product_category || product.productCategory || '',
      customCategory: product.custom_category || product.customCategory || '',
      length: product.length_cm?.toString() || product.length?.toString() || '',
      height: product.height_cm?.toString() || product.height?.toString() || '',
      width: product.width_cm?.toString() || product.width?.toString() || '',
      weight: product.weight_kg?.toString() || product.weight?.toString() || ''
    })),
    // Include tracking status fields
    ...trackingStatus
  };
};

const BookingRequests = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const [statusSearch, setStatusSearch] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [requestToShare, setRequestToShare] = useState(null);
  const [selectedAgency, setSelectedAgency] = useState('');
  const [agencySearch, setAgencySearch] = useState('');
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const [showShareConfirm, setShowShareConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null); // { requestId, newStatus }
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState({ title: '', message: '', type: 'info' }); // type: 'info', 'success', 'error'
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingRequest, setTrackingRequest] = useState(null);
  const [showTrackingConfirm, setShowTrackingConfirm] = useState(false);
  const [trackingAction, setTrackingAction] = useState(null); // { type: 'reached'|'departed'|'delivered', requestId, stopIndex, isStartLocation, isDestination }

  const statusDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);
  const shareModalRef = useRef(null);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Valid statuses from API: Pending, Contacted, Confirmed, Cancelled, Rejected
  const availableStatuses = ['Pending', 'Contacted', 'Confirmed', 'Cancelled', 'Rejected'];

  // Fetch agencies from API
  const fetchAgencies = useCallback(async () => {
    setLoadingAgencies(true);
    try {
      const response = await getAgencies({ page: 1, limit: 1000 }); // Fetch all agencies
      
      // Handle different response structures
      let agenciesData = [];
      if (Array.isArray(response.data)) {
        agenciesData = response.data;
      } else if (response.data && Array.isArray(response.data.items)) {
        agenciesData = response.data.items;
      } else if (Array.isArray(response)) {
        agenciesData = response;
      } else {
        agenciesData = response.data?.items || response.items || [];
      }
      
      // Map agencies to component format
      const mappedAgencies = agenciesData
        .filter(agency => agency.id && (agency.agency_name || agency.name || agency.company_name))
        .map(agency => ({
          id: agency.id || agency.agency_id,
          name: agency.agency_name || agency.name || agency.company_name || '',
          city: agency.city || '',
          state: agency.state || '',
          status: agency.status || 'Active'
        }));
      
      setAgencies(mappedAgencies);
    } catch (err) {
      console.error('Error fetching agencies:', err);
      setAgencies([]);
    } finally {
      setLoadingAgencies(false);
    }
  }, []);

  // Fetch agencies on component mount
  useEffect(() => {
    fetchAgencies();
  }, [fetchAgencies]);

  // Fetch booking requests from API
  const fetchBookingRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };

      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;

      const response = await getBookingRequests(params);

      // Handle response structure: { success, data: { items, total, limit, offset } }
      let requestsData = [];
      let paginationData = {};

      if (response?.data?.items && Array.isArray(response.data.items)) {
        requestsData = response.data.items;
        paginationData = {
          total: response.data.total || 0,
          limit: response.data.limit || itemsPerPage,
          offset: response.data.offset || 0
        };
      } else if (Array.isArray(response?.data)) {
        requestsData = response.data;
      } else if (Array.isArray(response)) {
        requestsData = response;
      }

      // Map API data to component format
      const mappedRequests = requestsData.map(mapBookingRequestFromAPI);
      setRequests(mappedRequests);
      setTotalCount(paginationData.total || mappedRequests.length);
    } catch (err) {
      console.error('Error fetching booking requests:', err);
      setError(err.message || 'Failed to fetch booking requests. Please try again.');
      setRequests([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, statusFilter]);

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchBookingRequests();
  }, [fetchBookingRequests]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, itemsPerPage]);

  // Calculate total pages from server-side pagination
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const paginatedRequests = requests; // Already paginated by server

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
      if (shareModalRef.current && !shareModalRef.current.contains(event.target)) {
        // Don't close if clicking inside modal
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, itemsPerPage]);

  const handleResetFilters = () => {
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handleViewRequest = async (request) => {
    try {
      // Get the numeric booking request ID (required for API)
      const numericId = request.numericId || request.id;
      
      if (!numericId || isNaN(parseInt(numericId, 10))) {
        // Fallback to using the request data we already have
        setViewingRequest(request);
        setShowViewModal(true);
        return;
      }
      
      // Convert to number
      const requestId = typeof numericId === 'string' ? parseInt(numericId, 10) : numericId;
      
      // Fetch full booking request details with products from API
      const response = await getBookingRequestById(requestId);
      
      // Extract booking request data from response
      const requestData = response.data || response;
      
      // Map API response to component format
      const mappedRequest = mapBookingRequestFromAPI(requestData);
      
      setViewingRequest(mappedRequest);
      setShowViewModal(true);
    } catch (err) {
      console.error('Error fetching booking request details:', err);
      // Fallback to using the request data we already have
      setViewingRequest(request);
      setShowViewModal(true);
      showMessage('Failed to load full booking request details. Showing available information.', 'Warning', 'error');
    }
  };

  const handleStatusUpdateClick = (requestIdParam, newStatus) => {
    // Show confirmation dialog first
    const request = viewingRequest;
    const numericId = request?.numericId || request?.id || requestIdParam;
    
    setPendingStatusUpdate({
      requestId: numericId,
      newStatus: newStatus,
      currentStatus: request?.status || 'Unknown'
    });
    setShowStatusConfirm(true);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!pendingStatusUpdate) return;

    try {
      const { requestId, newStatus } = pendingStatusUpdate;
      
      if (!requestId || isNaN(parseInt(requestId, 10))) {
        throw new Error('Invalid booking request ID. Please refresh the page and try again.');
      }
      
      // Convert to number
      const id = typeof requestId === 'string' ? parseInt(requestId, 10) : requestId;
      
      // Call API to update status
      await updateBookingRequestStatus(id, newStatus);
      
      // Refresh booking requests list
      await fetchBookingRequests();
      
      // Close confirmation and view modal
      setShowStatusConfirm(false);
      setPendingStatusUpdate(null);
      setShowViewModal(false);
      setViewingRequest(null);
      showMessage(`Booking request status updated to ${newStatus} successfully!`, 'Success', 'success');
    } catch (err) {
      console.error('Error updating booking request status:', err);
      
      // Extract error message from API response
      let errorMessage = err.message || 'Failed to update booking request status. Please try again.';
      
      // Check if it's a status transition error
      if (errorMessage.includes('Invalid status transition') || errorMessage.includes('status transition')) {
        const { currentStatus, newStatus } = pendingStatusUpdate;
        errorMessage = `Cannot change status from "${currentStatus}" to "${newStatus}".\n\nThis status transition is not allowed. Please select a valid status.`;
      }
      
      showMessage(errorMessage, 'Error', 'error');
      // Don't close the confirmation modal on error, so user can try a different status
      // setShowStatusConfirm(false);
      // setPendingStatusUpdate(null);
    }
  };

  const handleCancelStatusUpdate = () => {
    setShowStatusConfirm(false);
    setPendingStatusUpdate(null);
  };

  // Show custom message modal instead of alert
  const showMessage = (message, title = 'Message', type = 'info') => {
    setMessageModalContent({ title, message, type });
    setShowMessageModal(true);
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
    setMessageModalContent({ title: '', message: '', type: 'info' });
  };

  const handleShareRequest = (request) => {
    setRequestToShare(request);
    // Pre-select already shared agency
    setSelectedAgency(request.sharedWithAgency || '');
    setAgencySearch('');
    setShowShareModal(true);
  };

  const handleTrackingClick = async (request) => {
    try {
      // Get numericId for API call
      const numericId = request.numericId || 
                       (request.id && !isNaN(parseInt(request.id, 10)) ? parseInt(request.id, 10) : null);
      
      if (!numericId) {
        console.error('Invalid booking request ID for tracking:', request);
        showMessage('Unable to load tracking information. Invalid request ID.', 'Error', 'error');
        return;
      }

      // Fetch full booking request details with tracking information
      console.log('Fetching full booking request details for tracking, ID:', numericId);
      const response = await getBookingRequestById(numericId);
      
      // Extract the booking request data from response
      let requestData = null;
      if (response.data) {
        requestData = response.data.request || response.data.booking_request || response.data;
      } else if (response.request || response.booking_request) {
        requestData = response.request || response.booking_request;
      } else {
        requestData = response;
      }

      if (!requestData) {
        console.error('No booking request data in API response:', response);
        // Fallback to using the request from the list
        const latestRequest = requests.find(r => 
          (r.numericId && request.numericId && r.numericId === request.numericId) ||
          (r.id && request.id && r.id === request.id) ||
          (r.requestId && request.requestId && r.requestId === request.requestId)
        ) || request;
        setTrackingRequest(latestRequest);
        setShowTrackingModal(true);
        return;
      }

      // Log the raw API response to see what fields are available
      console.log('Raw API response for booking request:', JSON.stringify(requestData, null, 2));
      console.log('All keys in API response:', Object.keys(requestData));
      
      // Map the full booking request data (includes tracking status)
      const mappedRequest = mapBookingRequestFromAPI(requestData);
      
      console.log('Mapped tracking request with full details:', mappedRequest);
      console.log('Tracking status in mapped request:', {
        startLocationReached: mappedRequest.startLocationReached,
        startLocationDeparted: mappedRequest.startLocationDeparted,
        destinationReached: mappedRequest.destinationReached
      });
      setTrackingRequest(mappedRequest);
      setShowTrackingModal(true);
    } catch (err) {
      console.error('Error fetching booking request details for tracking:', err);
      // Fallback to using the request from the list if API call fails
      const latestRequest = requests.find(r => 
        (r.numericId && request.numericId && r.numericId === request.numericId) ||
        (r.id && request.id && r.id === request.id) ||
        (r.requestId && request.requestId && r.requestId === request.requestId)
      ) || request;
      
      // Ensure numericId is set for tracking
      if (!latestRequest.numericId) {
        if (latestRequest.id) {
          const parsedId = parseInt(latestRequest.id, 10);
          if (!isNaN(parsedId)) {
            latestRequest.numericId = parsedId;
          }
        }
      }
      
      console.log('Using request from list (fallback):', latestRequest);
      setTrackingRequest(latestRequest);
      setShowTrackingModal(true);
    }
  };

  // Update trackingRequest when requests change
  useEffect(() => {
    if (trackingRequest) {
      const updatedRequest = requests.find(r => r.id === trackingRequest.id);
      if (updatedRequest) {
        setTrackingRequest(updatedRequest);
      }
    }
  }, [requests]);

  const handleMarkReached = (requestId, stopIndex, isStartLocation = false, isDestination = false) => {
    setTrackingAction({
      type: isDestination ? 'delivered' : 'reached',
      requestId,
      stopIndex,
      isStartLocation,
      isDestination
    });
    setShowTrackingConfirm(true);
  };

  const handleMarkDeparted = (requestId, stopIndex, isStartLocation = false) => {
    setTrackingAction({
      type: 'departed',
      requestId,
      stopIndex,
      isStartLocation,
      isDestination: false
    });
    setShowTrackingConfirm(true);
  };

  const confirmTrackingAction = () => {
    if (!trackingAction) return;

    const { type, requestId, stopIndex, isStartLocation, isDestination } = trackingAction;

    setRequests(prevRequests => {
      const updated = prevRequests.map(req => {
        if (req.id === requestId) {
          if (type === 'reached') {
            if (isStartLocation) {
              return { 
                ...req, 
                startLocationReached: true,
                startLocationReachedTime: new Date().toLocaleString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric', 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                })
              };
            } else if (req.stops && req.stops[stopIndex]) {
              const updatedStops = [...req.stops];
              updatedStops[stopIndex] = {
                ...updatedStops[stopIndex],
                reached: true,
                reachedTime: new Date().toLocaleString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric', 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                })
              };
              return { ...req, stops: updatedStops };
            }
          } else if (type === 'delivered') {
            return { 
              ...req, 
              destinationReached: true,
              destinationReachedTime: new Date().toLocaleString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })
            };
          } else if (type === 'departed') {
            if (isStartLocation) {
              return { 
                ...req, 
                startLocationDeparted: true,
                startLocationDepartedTime: new Date().toLocaleString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric', 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                })
              };
            } else if (req.stops && req.stops[stopIndex]) {
              const updatedStops = [...req.stops];
              updatedStops[stopIndex] = {
                ...updatedStops[stopIndex],
                departed: true,
                departedTime: new Date().toLocaleString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric', 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                })
              };
              return { ...req, stops: updatedStops };
            }
          }
        }
        return req;
      });
      // Update trackingRequest
      const updatedRequest = updated.find(r => r.id === requestId);
      if (updatedRequest && trackingRequest && trackingRequest.id === requestId) {
        setTrackingRequest(updatedRequest);
      }
      return updated;
    });

    setShowTrackingConfirm(false);
    setTrackingAction(null);
  };

  const handleSelectAgency = (agencyId) => {
    setSelectedAgency(agencyId);
  };

  const handleProceedToConfirm = () => {
    if (requestToShare && selectedAgency) {
      setShowShareModal(false);
      setShowShareConfirm(true);
    }
  };

  const handleConfirmShare = async () => {
    if (!requestToShare || !selectedAgency) return;
    
    setIsSharing(true);
    try {
      // Get the numeric booking request ID (required for API)
      const numericId = requestToShare.numericId || requestToShare.id;
      
      if (!numericId || isNaN(parseInt(numericId, 10))) {
        throw new Error('Invalid booking request ID. Please refresh the page and try again.');
      }
      
      // Convert to number
      const requestId = typeof numericId === 'string' ? parseInt(numericId, 10) : numericId;
      
      // Convert agency ID to number if it's a string
      const agencyId = typeof selectedAgency === 'string' ? parseInt(selectedAgency, 10) : selectedAgency;
      
      if (isNaN(agencyId)) {
        throw new Error('Invalid agency ID. Please select an agency again.');
      }
      
      // Share booking request with agency using the share endpoint
      await shareBookingRequest(requestId, agencyId);
      
      // Refresh booking requests list
      await fetchBookingRequests();
      
      setShowShareConfirm(false);
      setShowShareSuccess(true);
      setRequestToShare(null);
      setSelectedAgency('');
      setTimeout(() => setShowShareSuccess(false), 3000);
    } catch (err) {
      console.error('Error sharing booking request:', err);
      showMessage(err.message || 'Failed to share booking request with agency. Please try again.', 'Error', 'error');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowShareConfirm(false);
    // Return to share modal with selected agency still selected
    setShowShareModal(true);
  };

  const getSharedAgencyName = (request) => {
    if (!request.sharedWithAgency) {
      return 'Not shared';
    }
    // Handle both numeric and string IDs
    const agency = agencies.find(a => 
      a.id === request.sharedWithAgency || 
      a.id?.toString() === request.sharedWithAgency?.toString() ||
      parseInt(a.id, 10) === parseInt(request.sharedWithAgency, 10)
    );
    return agency ? agency.name : `Agency ID: ${request.sharedWithAgency}`;
  };

  const getSelectedAgencyName = () => {
    if (!selectedAgency) return '';
    const agency = agencies.find(a => a.id === selectedAgency);
    return agency ? agency.name : selectedAgency;
  };

  const filteredAgencies = agencies.filter(agency =>
    agency.name.toLowerCase().includes(agencySearch.toLowerCase()) ||
    agency.city.toLowerCase().includes(agencySearch.toLowerCase())
  );

  const filteredStatuses = availableStatuses.filter(status =>
    status.toLowerCase().includes(statusSearch.toLowerCase())
  );

  const getStatusClass = (status) => {
    switch (status) {
      case 'Pending':
        return 'status-pending';
      case 'Contacted':
        return 'status-contacted';
      case 'Confirmed':
        return 'status-confirmed';
      case 'Cancelled':
        return 'status-cancelled';
      case 'Rejected':
        return 'status-rejected';
      default:
        return '';
    }
  };

  return (
    <div className="booking-requests-container">
      {/* Header */}
      <div className="requests-header">
        <div className="filter-group">
          <div className="filter-wrapper" ref={statusDropdownRef}>
            <button
              className={`filter-btn ${statusFilter ? 'active' : ''}`}
              onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            >
              <span>Status: {statusFilter || 'All'}</span>
              <span>▼</span>
            </button>
            {statusDropdownOpen && (
              <div className="filter-dropdown">
                <input
                  type="text"
                  placeholder="Search status..."
                  className="filter-search-input"
                  value={statusSearch}
                  onChange={(e) => setStatusSearch(e.target.value)}
                />
                <button
                  className={`filter-option ${!statusFilter ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('');
                    setStatusDropdownOpen(false);
                    setStatusSearch('');
                  }}
                >
                  All
                </button>
                {filteredStatuses.map(status => (
                  <button
                    key={status}
                    className={`filter-option ${statusFilter === status ? 'selected' : ''}`}
                    onClick={() => {
                      setStatusFilter(status);
                      setStatusDropdownOpen(false);
                      setStatusSearch('');
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className="btn-reset" onClick={handleResetFilters}>
            Reset Filters
          </button>
        </div>
      </div>

      <div className="show-entries-container">
        <div className="show-entries-wrapper" style={{ width: '100%' }}>
          <div className="show-entries-left" ref={showEntriesDropdownRef}>
            <label className="show-entries-label">Show</label>
            <button
              className="show-entries-btn"
              onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
            >
              <span>{itemsPerPage}</span>
              <span>▼</span>
            </button>
            {showEntriesDropdownOpen && (
              <div className="show-entries-dropdown">
                {[10, 25, 50, 100].map(num => (
                  <button
                    key={num}
                    className={`show-entries-option ${itemsPerPage === num ? 'selected' : ''}`}
                    onClick={() => {
                      setItemsPerPage(num);
                      setShowEntriesDropdownOpen(false);
                      setCurrentPage(1);
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            )}
            <label className="show-entries-label">entries</label>
          </div>
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, email, phone, location, vehicle, or request ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fee2e2', 
          border: '1px solid #fca5a5', 
          borderRadius: '6px', 
          color: '#dc2626', 
          marginBottom: '16px' 
        }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div className="requests-table-wrapper">
        <table className="requests-table">
          <thead>
            <tr>
              <th>Request ID</th>
              <th>Name</th>
              <th>Contact</th>
              <th>From Location</th>
              <th>To Location</th>
              <th>Vehicle Number</th>
              <th>Requested Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="no-data">
                  Loading booking requests...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="9" className="no-data">
                  {error}
                </td>
              </tr>
            ) : paginatedRequests.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  No booking requests found
                </td>
              </tr>
            ) : (
              paginatedRequests.map(request => (
                <tr key={request.id}>
                  <td>{request.requestId || request.id}</td>
                  <td>{request.name}</td>
                  <td>
                    <div className="contact-info">
                      <div>{request.email}</div>
                      <div className="phone-number">{request.phone}</div>
                    </div>
                  </td>
                  <td>{request.fromLocation}</td>
                  <td>{request.toLocation}</td>
                  <td>{request.vehicleNumber}</td>
                  <td>{request.requestedDate}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(request.status)}`}>
                      {request.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-view"
                        onClick={() => handleViewRequest(request)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button
                        className="btn-share"
                        onClick={() => handleShareRequest(request)}
                        title="Share to Transporter"
                      >
                        📤 Share
                      </button>
                      {(request.status === 'Contacted' || request.status === 'Confirmed') && (
                        <button
                          className="btn-tracking"
                          onClick={() => handleTrackingClick(request)}
                          title="Track Shipment"
                        >
                          📍 Track
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination-wrapper">
        <div className="pagination-info">
          <div className="pagination-count">
            Showing {totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
          </div>
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* View Request Modal */}
      {showViewModal && viewingRequest && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Booking Request Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="view-details">
                <div className="detail-row">
                  <span className="detail-label">Request ID:</span>
                  <span className="detail-value">{viewingRequest.requestId || viewingRequest.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{viewingRequest.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{viewingRequest.email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{viewingRequest.phone}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">From Location:</span>
                  <span className="detail-value">{viewingRequest.fromLocation}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">To Location:</span>
                  <span className="detail-value">{viewingRequest.toLocation}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Vehicle Number:</span>
                  <span className="detail-value">{viewingRequest.vehicleNumber}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Requested Date:</span>
                  <span className="detail-value">{viewingRequest.requestedDate}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value">
                    <span className={`status-badge ${getStatusClass(viewingRequest.status)}`}>
                      {viewingRequest.status}
                    </span>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Notes:</span>
                  <span className="detail-value">{viewingRequest.notes || 'No notes'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Shared With:</span>
                  <span className="detail-value">
                    {getSharedAgencyName(viewingRequest)}
                  </span>
                </div>
              </div>

              {/* Product Details Section */}
              {viewingRequest.products && viewingRequest.products.length > 0 && (
                <div className="product-details-section" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e0e0e0' }}>
                  <h3 className="section-title" style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '16px' }}>Product Details</h3>
                  {viewingRequest.products.map((product, index) => (
                    <div key={index} className="product-item-detail" style={{ marginBottom: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e3a8a', marginBottom: '12px' }}>
                        Product {index + 1}
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Product Name:</span>
                        <span className="detail-value">{product.productName}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Category:</span>
                        <span className="detail-value">
                          {product.productCategory === 'others' ? product.customCategory : product.productCategory.charAt(0).toUpperCase() + product.productCategory.slice(1)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Size:</span>
                        <span className="detail-value">
                          {product.length}cm × {product.height}cm × {product.width}cm
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Weight:</span>
                        <span className="detail-value">{product.weight} kg</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Update Status Section */}
              <div className="status-update-section">
                <h3 className="status-update-title">Update Status</h3>
                <div className="status-buttons">
                  {availableStatuses.map(status => (
                    <button
                      key={status}
                      className={`status-update-btn ${viewingRequest.status === status ? 'active' : ''}`}
                      onClick={() => handleStatusUpdateClick(viewingRequest.numericId || viewingRequest.id, status)}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-share" onClick={() => handleShareRequest(viewingRequest)}>
                📤 Share to Agency
              </button>
              <button className="btn-cancel" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share to Agency Modal */}
      {showShareModal && requestToShare && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content share-modal" ref={shareModalRef} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Share Booking Request to Transporter</h2>
              <button className="modal-close" onClick={() => setShowShareModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="share-request-info">
                <p><strong>Request ID:</strong> {requestToShare.requestId || requestToShare.id}</p>
                <p><strong>Customer:</strong> {requestToShare.name}</p>
                <p><strong>Route:</strong> {requestToShare.fromLocation} → {requestToShare.toLocation}</p>
              </div>

              <div className="agency-selection-section">
                <h3 className="section-title">Select Transporter</h3>
                <div className="agency-search-wrapper">
                  <input
                    type="text"
                    placeholder="Search transporters by name or city..."
                    className="agency-search-input"
                    value={agencySearch}
                    onChange={(e) => setAgencySearch(e.target.value)}
                  />
                  <span className="search-icon">🔍</span>
                </div>
                <div className="agencies-list">
                  {filteredAgencies.length === 0 ? (
                    <p className="no-agencies">No transporters found</p>
                  ) : (
                    filteredAgencies.map(agency => (
                      <label key={agency.id} className="agency-radio-item">
                        <input
                          type="radio"
                          name="selectedAgency"
                          value={agency.id}
                          checked={selectedAgency === agency.id}
                          onChange={() => handleSelectAgency(agency.id)}
                        />
                        <div className="agency-info">
                          <span className="agency-name">{agency.name}</span>
                          <span className="agency-location">{agency.city}, {agency.state}</span>
                        </div>
                        <span className={`agency-status ${agency.status.toLowerCase()}`}>
                          {agency.status}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                {selectedAgency && (
                  <div className="selected-count">
                    Selected: {getSelectedAgencyName()}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setShowShareModal(false);
                setSelectedAgency('');
                setRequestToShare(null);
                setAgencySearch('');
              }}>
                Cancel
              </button>
              <button 
                className="btn-confirm-share" 
                onClick={handleProceedToConfirm}
                disabled={!selectedAgency}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Confirmation Modal */}
      {showShareConfirm && requestToShare && selectedAgency && (
        <div className="modal-overlay" onClick={handleCancelConfirm}>
          <div className="modal-content confirm-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Share</h2>
              <button className="modal-close" onClick={handleCancelConfirm}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to share this booking request?</p>
              <div className="confirm-share-details">
                <div className="confirm-detail-row">
                  <span className="confirm-label">Request ID:</span>
                  <span className="confirm-value">{requestToShare.requestId || requestToShare.id}</span>
                </div>
                <div className="confirm-detail-row">
                  <span className="confirm-label">Customer:</span>
                  <span className="confirm-value">{requestToShare.name}</span>
                </div>
                <div className="confirm-detail-row">
                  <span className="confirm-label">Route:</span>
                  <span className="confirm-value">{requestToShare.fromLocation} → {requestToShare.toLocation}</span>
                </div>
                <div className="confirm-detail-row">
                  <span className="confirm-label">Share with Agency:</span>
                  <span className="confirm-value highlight">{getSelectedAgencyName()}</span>
                </div>
              </div>
              <p className="confirm-warning">This action will share the booking request with the selected agency.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCancelConfirm}>
                Cancel
              </button>
              <button 
                className="btn-confirm-share-final" 
                onClick={handleConfirmShare}
                disabled={isSharing}
              >
                {isSharing ? 'Sharing...' : 'Confirm Share'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Success Message */}
      {showShareSuccess && (
        <div className="success-toast">
          <span>✓ Booking request shared successfully!</span>
        </div>
      )}

      {/* Tracking Modal */}
      {showTrackingModal && trackingRequest && (
        <div className="modal-overlay" onClick={() => setShowTrackingModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Track Shipment - {trackingRequest.id}</h2>
              <button className="modal-close" onClick={() => setShowTrackingModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Start Location */}
                <div style={{ 
                  padding: '20px', 
                  background: '#f9fafb', 
                  borderRadius: '8px', 
                  border: '2px solid #e0e0e0',
                  borderLeft: '4px solid #10B981'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                      🏁 Start Location
                    </h3>
                    {trackingRequest.startLocationReached && (
                      <span style={{ 
                        padding: '4px 12px', 
                        background: '#10B981', 
                        color: 'white', 
                        borderRadius: '12px', 
                        fontSize: '0.85rem',
                        fontWeight: 500
                      }}>
                        ✓ Reached
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '8px 0', fontSize: '1rem', fontWeight: 500, color: '#111827' }}>
                    {trackingRequest.fromLocation}
                  </p>
                  {trackingRequest.startLocationReachedTime && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                      Reached: {trackingRequest.startLocationReachedTime}
                    </p>
                  )}
                  {trackingRequest.startLocationDepartedTime && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                      Departed: {trackingRequest.startLocationDepartedTime}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    {!trackingRequest.startLocationReached && (
                      <button
                        onClick={() => handleMarkReached(trackingRequest.id, -1, true, false)}
                        style={{
                          padding: '8px 16px',
                          background: '#10B981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}
                      >
                        ✓ Mark Reached
                      </button>
                    )}
                    {trackingRequest.startLocationReached && !trackingRequest.startLocationDeparted && (
                      <button
                        onClick={() => handleMarkDeparted(trackingRequest.id, -1, true)}
                        style={{
                          padding: '8px 16px',
                          background: '#EF4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}
                      >
                        🚚 Mark Departed
                      </button>
                    )}
                  </div>
                </div>

                {/* Intermediate Stops */}
                {trackingRequest.stops && trackingRequest.stops.length > 0 && (
                  <>
                    {trackingRequest.stops.map((stop, index) => (
                      <div key={index} style={{ 
                        padding: '20px', 
                        background: '#f9fafb', 
                        borderRadius: '8px', 
                        border: '2px solid #e0e0e0',
                        borderLeft: '4px solid #2563EB'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                            📍 Stop {index + 1}: {stop.location}
                          </h3>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {stop.reached && (
                              <span style={{ 
                                padding: '4px 12px', 
                                background: '#10B981', 
                                color: 'white', 
                                borderRadius: '12px', 
                                fontSize: '0.85rem',
                                fontWeight: 500
                              }}>
                                ✓ Reached
                              </span>
                            )}
                            {stop.departed && (
                              <span style={{ 
                                padding: '4px 12px', 
                                background: '#EF4444', 
                                color: 'white', 
                                borderRadius: '12px', 
                                fontSize: '0.85rem',
                                fontWeight: 500
                              }}>
                                🚚 Departed
                              </span>
                            )}
                          </div>
                        </div>
                        {stop.reachedTime && (
                          <p style={{ margin: '4px 0', fontSize: '0.85rem', color: '#6b7280' }}>
                            Reached: {stop.reachedTime}
                          </p>
                        )}
                        {stop.departedTime && (
                          <p style={{ margin: '4px 0', fontSize: '0.85rem', color: '#6b7280' }}>
                            Departed: {stop.departedTime}
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                          {!stop.reached && (
                            <button
                              onClick={() => handleMarkReached(trackingRequest.id, index, false, false)}
                              style={{
                                padding: '8px 16px',
                                background: '#10B981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: 500
                              }}
                            >
                              ✓ Mark Reached
                            </button>
                          )}
                          {stop.reached && !stop.departed && (
                            <button
                              onClick={() => handleMarkDeparted(trackingRequest.id, index, false)}
                              style={{
                                padding: '8px 16px',
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: 500
                              }}
                            >
                              🚚 Mark Departed
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Destination */}
                <div style={{ 
                  padding: '20px', 
                  background: '#f9fafb', 
                  borderRadius: '8px', 
                  border: '2px solid #e0e0e0',
                  borderLeft: '4px solid #EF4444'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                      🎯 Destination
                    </h3>
                    {trackingRequest.destinationReached && (
                      <span style={{ 
                        padding: '4px 12px', 
                        background: '#10B981', 
                        color: 'white', 
                        borderRadius: '12px', 
                        fontSize: '0.85rem',
                        fontWeight: 500
                      }}>
                        ✓ Delivered
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '8px 0', fontSize: '1rem', fontWeight: 500, color: '#111827' }}>
                    {trackingRequest.toLocation}
                  </p>
                  {trackingRequest.destinationReachedTime && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                      Delivered: {trackingRequest.destinationReachedTime}
                    </p>
                  )}
                  {!trackingRequest.destinationReached && (
                    <div style={{ marginTop: '12px' }}>
                      <button
                        onClick={() => handleMarkReached(trackingRequest.id, -1, false, true)}
                        style={{
                          padding: '8px 16px',
                          background: '#10B981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}
                      >
                        ✓ Mark Delivered
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowTrackingModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Confirmation Modal */}
      {showStatusConfirm && pendingStatusUpdate && viewingRequest && (
        <div className="modal-overlay" onClick={handleCancelStatusUpdate}>
          <div className="modal-content confirm-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Status Update</h2>
              <button className="modal-close" onClick={handleCancelStatusUpdate}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to update the status of this booking request?</p>
              <div className="confirm-share-details">
                <div className="confirm-detail-row">
                  <span className="confirm-label">Request ID:</span>
                  <span className="confirm-value">{viewingRequest.requestId || viewingRequest.id}</span>
                </div>
                <div className="confirm-detail-row">
                  <span className="confirm-label">Customer:</span>
                  <span className="confirm-value">{viewingRequest.name}</span>
                </div>
                <div className="confirm-detail-row">
                  <span className="confirm-label">Current Status:</span>
                  <span className="confirm-value">{pendingStatusUpdate.currentStatus}</span>
                </div>
                <div className="confirm-detail-row">
                  <span className="confirm-label">New Status:</span>
                  <span className="confirm-value highlight">{pendingStatusUpdate.newStatus}</span>
                </div>
              </div>
              <p className="confirm-warning">This action will change the booking request status from "{pendingStatusUpdate.currentStatus}" to "{pendingStatusUpdate.newStatus}".</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCancelStatusUpdate}>
                Cancel
              </button>
              <button 
                className="btn-confirm-share-final" 
                onClick={handleConfirmStatusUpdate}
              >
                Confirm Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div className="modal-overlay" onClick={closeMessageModal}>
          <div className="modal-content message-modal" onClick={(e) => e.stopPropagation()}>
            {messageModalContent.type !== 'error' && (
              <div className="modal-header">
                <h2>{messageModalContent.title}</h2>
                <button className="modal-close" onClick={closeMessageModal}>×</button>
              </div>
            )}
            {messageModalContent.type === 'error' && (
              <div className="modal-header message-header-error">
                {/* No close button for error messages */}
              </div>
            )}
            <div className="modal-body">
              <div className={`message-content message-${messageModalContent.type}`}>
                {messageModalContent.message.split('\n').map((line, index) => (
                  <p key={index} style={{ margin: index > 0 ? '8px 0 0 0' : '0' }}>
                    {line}
                  </p>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className={`btn-confirm-share-final ${messageModalContent.type === 'error' ? 'btn-error' : messageModalContent.type === 'success' ? 'btn-success' : ''}`}
                onClick={closeMessageModal}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Confirmation Modal */}
      {showTrackingConfirm && trackingAction && trackingRequest && (
        <div className="modal-overlay" onClick={() => setShowTrackingConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Confirm Tracking Update</h2>
              <button className="modal-close" onClick={() => setShowTrackingConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '1rem', color: '#333', margin: '16px 0' }}>
                {trackingAction.type === 'reached' && trackingAction.isStartLocation && 
                  `Are you sure you want to mark the start location "${trackingRequest.fromLocation}" as reached?`}
                {trackingAction.type === 'reached' && !trackingAction.isStartLocation && !trackingAction.isDestination && 
                  `Are you sure you want to mark stop "${trackingRequest.stops?.[trackingAction.stopIndex]?.location}" as reached?`}
                {trackingAction.type === 'delivered' && 
                  `Are you sure you want to mark the destination "${trackingRequest.toLocation}" as delivered?`}
                {trackingAction.type === 'departed' && trackingAction.isStartLocation && 
                  `Are you sure you want to mark the start location "${trackingRequest.fromLocation}" as departed?`}
                {trackingAction.type === 'departed' && !trackingAction.isStartLocation && 
                  `Are you sure you want to mark stop "${trackingRequest.stops?.[trackingAction.stopIndex]?.location}" as departed?`}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowTrackingConfirm(false)}>
                Cancel
              </button>
              <button 
                className="btn-share" 
                onClick={confirmTrackingAction}
                style={{ background: '#10B981' }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingRequests;

